/**
 * Patient Registration Page
 * Allows new patients to create an account with email/password
 */
import React, { useState, useEffect } from 'react';
import { DateOfBirthField, validateDobIso } from '../components/patient/DateOfBirthField';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';
import { submitPendingTreatmentIntent } from '../services/TreatmentIntentService';
import { submitPendingQuoteRequest } from '../components/QuoteRequestModal';
import { mergeConsentOnLogin } from '../services/CookieConsentService';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { EmailOtpStep } from '../components/EmailOtpStep';
import { LegalRequirements } from '../components/legal/LegalRequirements';
import axios from 'axios';
// TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 2 — replaces the previous
// free-text phone field with a country-code + national-digits input
// that emits an E.164 string. Storage and API contract are unchanged
// — `formData.phone` continues to be a string, now always canonical.
import { PhoneInput } from '../components/ui/phone-input';
// `useContent` import removed with the retired `patient_register_consent_text`
// CMS fetch (TREATX-LEGAL-CORPUS-CLEANUP-AC-01).

// Minimal country list used at registration. Each item: [ISO2, display].
// Jurisdictional legal requirements are resolved server-side per country
// via the canonical journey requirements endpoint (legal_policy_bindings).
const REG_COUNTRIES = [
  ['TR','Türkiye'],
  ['DE','Germany'], ['FR','France'], ['IT','Italy'], ['ES','Spain'],
  ['NL','Netherlands'], ['BE','Belgium'], ['SE','Sweden'], ['IE','Ireland'],
  ['AT','Austria'], ['PL','Poland'], ['GR','Greece'], ['PT','Portugal'],
  ['DK','Denmark'], ['FI','Finland'], ['CZ','Czechia'], ['RO','Romania'],
  ['HU','Hungary'], ['NO','Norway'], ['CH','Switzerland'],
  ['GB','United Kingdom'],
  ['US','United States'], ['CA','Canada'],
  ['AE','United Arab Emirates'], ['SA','Saudi Arabia'], ['QA','Qatar'],
  ['IN','India'], ['SG','Singapore'], ['AU','Australia'],
  ['XX','Other'],
];

const PatientRegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  // CMS content (body preview only — versions authoritatively come
  // from the canonical Agreement Management registry, fetched below).

  // TREATX-LEGAL-SSOT-CANON-01 Phase 2 — canonical journey-driven legal
  // requirements. The page knows ONLY journey = patient_registration; the
  // server resolves which documents apply from legal_policy_bindings.
  const [legalState, setLegalState] = useState({ payload: [], blockingOk: false, ready: false, count: 0 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    country: '',
    date_of_birth: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // TREATX-PATIENT-DOCTOR-EMAIL-OTP-PRECREATE-01: 2-step flow.
  //   step = 'form'  → user fills the registration form
  //   step = 'otp'   → user enters the 6-digit code we emailed them
  // The actual /api/auth/patient/register call only fires AFTER the
  // OTP step succeeds. No `rbac_users`, `persons`, or JWT exists
  // before that point.
  const [step, setStep] = useState('form');
  const [otpError, setOtpError] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(600);
  const [otpResendAfter, setOtpResendAfter] = useState(60);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // TREATX-LEGAL-SSOT-CANON-01 Phase 2 — the authoritative agreement list
  // for this journey is now resolved server-side by <LegalRequirements/>
  // from legal_policy_bindings; no client-side agreement fetch needed here.

  // Country-aware jurisdiction context (passed to the canonical resolver).
  const country = formData.country;

  // Submit gate derives from the canonical requirements: all BLOCKING
  // documents for the journey must be accepted. No hard-coded doc keys.
  const mandatoryConsentsSatisfied =
    !!country && legalState.ready && legalState.blockingOk;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!country) {
      newErrors.country = 'Country of residence is required';
    }

    // TREATX-L1-A / P0-DOB-ONBOARDING-01 — mandatory Date of Birth with the
    // SHARED validator used by every other DoB entry point (calendar-correct,
    // leap-year safe, no timezone drift). Backend remains authoritative
    // (mirrors DOB_REQUIRED / DOB_INVALID / PATIENT_UNDER_18 response codes).
    const dobProblem = validateDobIso(formData.date_of_birth);
    if (dobProblem) {
      newErrors.date_of_birth = t(dobProblem);
    }

    if (!legalState.blockingOk) {
      newErrors.consent_base = t('legal.mustAcceptRequired', 'You must accept all required legal documents to continue');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // TREATX-PATIENT-DOCTOR-EMAIL-OTP-PRECREATE-01:
    // Step 1 of the form-submit handler now ONLY requests the email
    // OTP. We must not call the canonical register endpoint until the
    // OTP step verifies email ownership.
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/auth/email-otp/request`, {
        email: formData.email,
        purpose: 'patient_register',
        name: formData.name,
      });
      const data = resp.data || {};
      setOtpExpiresIn(Number(data.expires_in) || 600);
      setOtpResendAfter(Number(data.resend_after) || 60);
      setOtpError('');
      setStep('otp');
      toast.success('We sent a verification code to your email.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Could not send the verification code. Please try again.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object' && detail.message) {
        msg = detail.message;
      } else {
        const respMsg = err?.response?.data?.message;
        if (typeof respMsg === 'string') {
          const m = respMsg.match(/'message'\s*:\s*'([^']+)'/);
          if (m) msg = m[1];
          else msg = respMsg;
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Resend the OTP — same backend endpoint, returns 429 inside the
  // 60-second cooldown window. The OTP step component owns its own
  // countdown UI; we surface server errors via toast.
  const handleResendOtp = async () => {
    setOtpError('');
    try {
      const resp = await axios.post(`${API_URL}/api/auth/email-otp/request`, {
        email: formData.email,
        purpose: 'patient_register',
        name: formData.name,
      });
      const data = resp.data || {};
      setOtpExpiresIn(Number(data.expires_in) || 600);
      setOtpResendAfter(Number(data.resend_after) || 60);
      toast.success('A new code is on the way.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' ? detail : 'Resend failed. Please wait a moment and try again.');
      toast.error(msg);
    }
  };

  // After the user enters the OTP we POST the canonical
  // /api/auth/patient/register payload with `otp_code`. The backend
  // will reject the request with HTTP 400/422/429 if the OTP fails,
  // and ONLY create rbac_users + persons + JWT on success.
  const handleVerifyOtpAndRegister = async (code) => {
    setLoading(true);
    setOtpError('');
    try {
      const response = await authAPI.patientRegister({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone || null,
        date_of_birth: formData.date_of_birth,
        otp_code: code,
        // PHASE 2B/B1 — declared country of residence → persons.residence_country
        country_context: formData.country || null,
        // TREATX-LEGAL-SSOT-CANON-01 Phase 2 — the canonical acceptance
        // payload (content_key + version + ACTUAL presented locale). The
        // backend independently re-resolves what is required and writes
        // user_consents evidence ATOMICALLY with account creation.
        consents: legalState.payload,
      });
      
      if (response.data.success) {
        const { token, patient, person_id } = response.data;

        // SECURITY-S4: canonical unified surface only.
        localStorage.setItem('treatx_unified_user', JSON.stringify({
          id: patient.id,
          person_id: person_id || patient.id,
          email: patient.email,
          name: patient.name,
          role: 'patient',
          phone: patient.phone,
        }));

        const userData = {
          id: patient.id,
          person_id: person_id || patient.id,
          email: patient.email,
          name: patient.name,
          role: 'patient',
          phone: patient.phone,
        };
        login(userData);
        
        // P1-001 EXT: Merge anonymous consent records to this user
        mergeConsentOnLogin();

        // NOTE: legal acceptance evidence is written server-side, atomically,
        // during POST /api/auth/patient/register (see canonical enforcement).
        // No post-registration accept-batch call is needed.

        toast.success(`Welcome to TreatX, ${patient.name}!`);
        
        // P1-010: Submit any pending intents after signup
        let intentHandled = false;
        
        // Try pending quote request first
        try {
          const pendingResult = await submitPendingQuoteRequest(token);
          if (pendingResult?.success) {
            toast.success(`Quote request sent to ${pendingResult.clinic_name || 'the clinic'}!`);
            intentHandled = true;
          }
        } catch (err) {
          console.error('Failed to submit pending quote after signup:', err);
        }
        
        // Then try treatment intent
        if (!intentHandled) {
          try {
            const intentResult = await submitPendingTreatmentIntent();
            if (intentResult?.success) {
              const msg = intentResult.deduplicated
                ? `You already have a request for ${intentResult.treatment || 'this treatment'}.`
                : `Request created for ${intentResult.treatment || 'your selected treatment'}!`;
              toast.success(msg);
              intentHandled = true;
            }
          } catch (err) {
            console.error('Failed to submit treatment intent after signup:', err);
          }
        }
        
        // Navigate to dashboard (shows "Your Requests" with the new lead)
        navigate(intentHandled ? '/patient/dashboard' : '/patient/onboarding');
      }
    } catch (error) {
      // Map OTP-specific failures back to the inline OTP step so the
      // user can correct the code without losing the form.
      const detail = error?.response?.data?.detail;
      const respMsg = error?.response?.data?.message;
      const code = (detail && typeof detail === 'object' && detail.code)
        || (typeof respMsg === 'string' ? (respMsg.match(/'code'\s*:\s*'([a-zA-Z0-9_]+)'/) || [])[1] : null);
      const msg = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' ? detail : null)
        || (typeof respMsg === 'string' ? (respMsg.match(/'message'\s*:\s*'([^']+)'/) || [])[1] : null)
        || 'Registration failed. Please try again.';
      const otpCodes = new Set([
        'otp_invalid', 'otp_expired', 'otp_not_found',
        'otp_too_many_attempts', 'otp_required', 'otp_invalid_format',
      ]);
      if (otpCodes.has(code)) {
        setOtpError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 px-4 py-8" data-testid="patient-register-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          {/* Back Link */}
          <Link to="/login" className="inline-flex items-center text-sm text-foreground/60 hover:text-primary mb-6">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </Link>

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">T</span>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-secondary text-center mb-2">{t('patient.register.title')}</h1>
          <p className="text-foreground/70 text-center mb-6">{t('patient.register.pageIntro')}</p>

          {step === 'otp' ? (
            <EmailOtpStep
              email={formData.email}
              cooldownSeconds={otpResendAfter}
              expiresInSeconds={otpExpiresIn}
              loading={loading}
              errorMsg={otpError}
              onVerify={handleVerifyOtpAndRegister}
              onResend={handleResendOtp}
              onBack={() => { setStep('form'); setOtpError(''); }}
              testIdPrefix="patient-otp"
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('patient.auth.fullName')}</label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                  placeholder={t('patient.register.fullNamePlaceholder')}
                  data-testid="register-name-input"
                />
              </div>
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('patient.auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder={t('patient.auth.email')}
                  data-testid="register-email-input"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Phone Field (Optional) — E.164 normalized via PhoneInput */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('patient.register.phone')} <span className="text-foreground/50">{t('patient.register.phoneOptional')}</span></label>
              <PhoneInput
                value={formData.phone}
                onChange={(e164) => setFormData((prev) => ({ ...prev, phone: e164 }))}
                testId="register-phone-input"
                placeholder={t('patient.register.phonePlaceholder')}
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('patient.auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('patient.auth.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* TREATX-CONSENTS-WIRE-01: country + consent block */}
            <div className="space-y-3 pt-2 border-t border-slate-100 mt-2">
              <div>
                <label htmlFor="reg-country" className="block text-sm font-medium text-foreground/80 mb-1">
                  Country of Residence <span className="text-red-500">*</span>
                </label>
                <select
                  id="reg-country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.country ? 'border-red-500' : 'border-slate-200'} bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30`}
                  data-testid="register-country-select"
                >
                  <option value="">{t('patient.travel.departureCountry')}…</option>
                  {REG_COUNTRIES.map(([code, label]) => (
                    <option key={code} value={code}>{label}</option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              {/* TREATX-L1-A / P0-DOB-ONBOARDING-01 — mandatory Date of Birth
                  (18+) through the ONE shared patient DoB field: type it or
                  pick it from the calendar, canonical ISO either way. Backend
                  remains authoritative. */}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  {t('patient.register.dateOfBirth', 'Date of birth')} <span className="text-red-500">*</span>
                </label>
                <DateOfBirthField
                  value={formData.date_of_birth}
                  onChange={(iso) => setFormData((prev) => ({ ...prev, date_of_birth: iso }))}
                  label={null}
                  testIdPrefix="register-dob"
                />
                {errors.date_of_birth && (
                  <p className="text-red-500 text-xs mt-1" data-testid="register-dob-error">
                    {errors.date_of_birth}
                  </p>
                )}
              </div>

              <LegalRequirements
                journeyKey="patient_registration"
                country={country}
                onChange={setLegalState}
              />
              {errors.consent_base && <p className="text-red-500 text-xs">{errors.consent_base}</p>}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full py-6 text-lg font-semibold"
              disabled={loading || !mandatoryConsentsSatisfied}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Create Account
                </span>
              )}
            </Button>
          </form>
          )}

          {/* Login Link */}
          <p className="text-center text-sm text-foreground/60 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>

            {/* Terms — static copy; the retired `patient_register_consent_text`
                CMS fetch was removed by TREATX-LEGAL-CORPUS-CLEANUP-AC-01 */}
          <p className="text-center text-xs text-foreground/50 mt-4">
            By creating an account you agree to our{' '}
            <a href="/#/terms" className="underline hover:text-foreground/70">{t('patient.register.termsOfService')}</a>
            {' '}and{' '}
            <a href="/#/privacy" className="underline hover:text-foreground/70">{t('patient.register.privacyPolicy')}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PatientRegisterPage;
